/**
 * `/projects/:id` — the project overview (`ProjectOverview.dc.html`).
 *
 * One payload, computed server-side (§7.3). The screen's job is to answer three
 * questions honestly: what is this project, how much of the checklist applies to it, and
 * what does its last closed audit say. A project that has never been audited has **no
 * score** — which is not the same as scoring zero, and the screen says so in words.
 */

import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { CHECKLIST_CATEGORY_NAMES, TEAM_ROLES, can, checklistCategoryName, teamRoleName } from '@portal/contracts';
import type { CategoryScopeDto, PatchProjectInput, ProjectOverview, TeamRole_ } from '@portal/contracts';
import type { ContextProfile } from '@portal/domain';
import {
  Avatar,
  LockGlyph,
  PermissionButton,
  RepositoryGlyph,
  ScoreRing,
  SectionBar,
  WarningGlyph,
  maturityLabel,
} from '@portal/ui';

import { ApiClientError, api } from '../api.js';

const LABEL: CSSProperties = {
  fontSize: 'var(--fs-label)',
  fontWeight: 600,
  letterSpacing: 'var(--tracking-label)',
  textTransform: 'uppercase',
  color: 'var(--mu)',
};

const CHIP: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 20,
  padding: '0 6px',
  borderRadius: 'var(--r-md)',
  border: '1px solid var(--bd)',
  background: 'var(--neb)',
  color: 'var(--mu)',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

/** The identity fields PATCH /projects/:id accepts. Context is a separate record. */
const CRITICALITY = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'] as const;
const DATA_CLASSIFICATION = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'] as const;
const EXPOSURE = ['INTERNAL', 'PARTNER', 'PUBLIC'] as const;

const INPUT: CSSProperties = {
  height: 30,
  padding: '0 9px',
  border: '1px solid var(--bd)',
  borderRadius: 'var(--r-md)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  font: '400 12.5px/1 var(--font-sans)',
  width: '100%',
};

type Member = ProjectOverview['members'][number];

/** An assignment being written. `memberId` null means it is a new one. */
interface MemberDraft {
  memberId: string | null;
  userId: string;
  role: TeamRole_;
  isBackup: boolean;
}

const FLAG_LABELS: Array<[keyof ContextProfile, string]> = [
  ['processesPersonalData', 'Personal data'],
  ['publiclyExposed', 'Public-facing'],
  ['exposesPublicApi', 'Public API'],
  ['wizInScope', 'Wiz in scope'],
];

function Card({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--sf)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--r-lg)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '11px 14px',
          borderBottom: '1px solid var(--bd)',
        }}
      >
        <h2 style={{ margin: 0, font: '600 13px/1.3 var(--font-sans)' }}>{title}</h2>
        {action !== undefined && <span style={{ marginLeft: 'auto' }}>{action}</span>}
      </div>
      {children}
    </section>
  );
}

/**
 * A link-weight action that still obeys section 8.4.
 *
 * `PermissionButton` is the right thing for a real button, but these two sit in a row of
 * 20px chips and a pair of 30px buttons there reads as a toolbar. What matters is that a
 * control the user cannot use is still SHOWN, greyed, with a lock and the reason on
 * hover — hiding it makes people think the product is broken.
 */
function TextAction({
  label,
  onClick,
  disabledReason,
}: {
  label: string;
  onClick: () => void;
  disabledReason: string | null;
}) {
  const disabled = disabledReason !== null;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabledReason ?? undefined}
      aria-disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        background: 'none',
        border: 'none',
        padding: 0,
        color: disabled ? 'var(--mu)' : 'var(--ac)',
        font: '400 11.5px/1 var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {disabled && <LockGlyph size={10} />}
      {label}
    </button>
  );
}

function Modal({ width, children }: { width: number; children: ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,24,39,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 10,
      }}
    >
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--r-lg)',
          width,
          maxHeight: '90vh',
          overflow: 'auto',
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function ProjectOverviewRoute() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [showContextEditor, setShowContextEditor] = useState(false);
  const [draft, setDraft] = useState<ContextProfile | null>(null);
  const [justification, setJustification] = useState('');
  const [showCategoryEditor, setShowCategoryEditor] = useState(false);
  const [categoryDraft, setCategoryDraft] = useState<CategoryScopeDto | null>(null);
  const [categoryJustification, setCategoryJustification] = useState('');
  const [details, setDetails] = useState<PatchProjectInput | null>(null);
  const [memberDraft, setMemberDraft] = useState<MemberDraft | null>(null);

  const overview = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.project(id),
    enabled: id !== '',
  });

  // Both needed for the team editor: who may change it, and who can be assigned.
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  const people = useQuery({ queryKey: ['users'], queryFn: api.users });

  const preview = useQuery({
    queryKey: ['project', id, 'context-preview', draft],
    queryFn: () => api.previewContext(id, draft as ContextProfile),
    enabled: draft !== null,
  });

  const commit = useMutation({
    mutationFn: () => api.commitContext(id, draft as ContextProfile, justification),
    onSuccess: async () => {
      setShowContextEditor(false);
      setDraft(null);
      setJustification('');
      await queryClient.invalidateQueries({ queryKey: ['project', id] });
      await queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  const categoryPreview = useQuery({
    queryKey: ['project', id, 'category-scope-preview', categoryDraft],
    queryFn: () => api.previewCategoryScope(id, categoryDraft as CategoryScopeDto),
    enabled: categoryDraft !== null,
  });

  const commitCategoryScope = useMutation({
    mutationFn: () =>
      api.commitCategoryScope(id, categoryDraft as CategoryScopeDto, categoryJustification),
    onSuccess: async () => {
      setShowCategoryEditor(false);
      setCategoryDraft(null);
      setCategoryJustification('');
      await queryClient.invalidateQueries({ queryKey: ['project', id] });
      await queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  /** Everything the team and details editors write refreshes the same two caches. */
  const afterWrite = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['project', id] });
    await queryClient.invalidateQueries({ queryKey: ['portfolio'] });
  };

  const saveDetails = useMutation({
    mutationFn: (input: PatchProjectInput) => api.patchProject(id, input),
    onSuccess: async () => {
      setDetails(null);
      await afterWrite();
    },
  });

  const saveMember = useMutation({
    mutationFn: (input: MemberDraft) =>
      input.memberId === null
        ? api.assignMember(id, { userId: input.userId, role: input.role, isBackup: input.isBackup })
        : api.patchMember(id, input.memberId, {
            userId: input.userId,
            role: input.role,
            isBackup: input.isBackup,
          }),
    onSuccess: async () => {
      setMemberDraft(null);
      await afterWrite();
    },
  });

  const removeMember = useMutation({
    mutationFn: (memberId: string) => api.removeMember(id, memberId),
    onSuccess: afterWrite,
  });

  const acknowledge = useMutation({
    mutationFn: (memberId: string) => api.acknowledgeMembership(id, memberId),
    onSuccess: afterWrite,
  });

  if (overview.isPending) {
    return <div style={{ padding: '24px 28px', color: 'var(--mu)', fontSize: 13 }}>Loading…</div>;
  }

  if (overview.isError || overview.data === undefined) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 620 }}>
        <h1 style={{ margin: '0 0 8px', font: '600 19px/1.2 var(--font-sans)' }}>
          This project could not be loaded
        </h1>
        <p style={{ fontSize: 12.5, color: 'var(--mu)', lineHeight: 1.6 }}>
          {overview.error instanceof ApiClientError
            ? overview.error.message
            : 'Something went wrong on our side.'}
        </p>
        <Link to="/" style={{ fontSize: 12.5 }}>Back to the portfolio</Link>
      </div>
    );
  }

  const data = overview.data;
  const published = data.score.published;

  /**
   * The same `can()` the API guard uses, so the greyed reason on screen is the reason the
   * server would actually give (ADR 0003, one implementation shared by both callers).
   */
  const subject = {
    orgRole: me.data?.orgRole ?? ('ReadOnly' as const),
    projectRoles: me.data?.memberships.find((entry) => entry.projectId === id)?.roles ?? [],
  };
  const reasonFor = (
    capability: 'project.edit' | 'project.editContext' | 'project.editCategoryScope' | 'project.manageMembers',
  ) => {
    // While /me is in flight nobody's role is known yet; saying so beats flashing a
    // "your role cannot do this" that turns out to be false a moment later.
    if (me.isPending) return 'Checking your permissions…';
    const verdict = can(capability, subject);
    return verdict.allowed ? null : (verdict.reason ?? 'Not permitted.');
  };
  const cannotEdit = reasonFor('project.edit');
  const cannotManageTeam = reasonFor('project.manageMembers');

  return (
    <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--mu)' }}>
          <Link to="/">Portfolio</Link>
          <span>›</span>
          <span style={{ color: 'var(--tx)', fontWeight: 600 }}>{data.project.name}</span>
          <Link to={`/projects/${id}/waivers`} style={{ marginLeft: 'auto', fontSize: 12.5 }}>
            Waivers
          </Link>
          <Link to={`/projects/${id}/reports`} style={{ fontSize: 12.5 }}>
            Reports &amp; KPIs
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 style={{ margin: 0, font: '600 21px/1.1 var(--font-sans)', letterSpacing: '-0.01em' }}>
                {data.project.name}
              </h1>
              <span style={{ fontSize: 13, color: 'var(--mu)' }}>{data.project.clientName}</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={CHIP}>Criticality: {data.project.criticality}</span>
              <span style={CHIP}>Data: {data.project.dataClassification}</span>
              <span style={CHIP}>Exposure: {data.project.exposure}</span>
              <span style={CHIP}>{data.project.hostingRegion}</span>
              <span style={{ width: 1, height: 16, background: 'var(--bd)', margin: '0 3px' }} />
              {FLAG_LABELS.filter(([key]) => data.context[key] === true).map(([key, label]) => (
                <span key={key} style={{ ...CHIP, background: 'transparent' }}>{label}</span>
              ))}
              <span style={CHIP}>
                Categories:{' '}
                {data.categoryScope === 'ALL'
                  ? 'All'
                  : data.categoryScope.map((category) => checklistCategoryName(category)).join(', ')}
              </span>
              <TextAction
                label="Edit details"
                disabledReason={cannotEdit}
                onClick={() =>
                  setDetails({
                    name: data.project.name,
                    clientName: data.project.clientName,
                    criticality: data.project.criticality,
                    dataClassification: data.project.dataClassification,
                    exposure: data.project.exposure,
                    hostingRegion: data.project.hostingRegion,
                    gitlabGroupId: data.project.gitlabGroupId,
                  })
                }
              />
              {/*
                Gated now too. It was not before, so someone without the capability got a
                403 from a control that looked available — the exact failure section 8.4
                exists to prevent.
              */}
              <TextAction
                label="Edit context"
                disabledReason={reasonFor('project.editContext')}
                onClick={() => {
                  setDraft(data.context);
                  setShowContextEditor(true);
                }}
              />
              <TextAction
                label="Edit category scope"
                disabledReason={reasonFor('project.editCategoryScope')}
                onClick={() => {
                  setCategoryDraft(data.categoryScope);
                  setShowCategoryEditor(true);
                }}
              />
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ display: 'flex' }}>
              {data.members.map((member, index) => (
                <span key={member.userId + member.role} style={{ marginLeft: index === 0 ? 0 : -5 }}>
                  <Avatar initials={member.initials} name={`${member.displayName} · ${member.role}`} />
                </span>
              ))}
            </div>
            <Link
              to={`/projects/${id}/audits/new`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                height: 30,
                padding: '0 12px',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--ac)',
                background: 'var(--ac)',
                color: '#fff',
                font: '500 12.5px/1 var(--font-sans)',
                textDecoration: 'none',
              }}
            >
              New audit
            </Link>
          </div>
        </div>
      </header>

      {/* Score band. Honest about the difference between "no audit yet" and "zero". */}
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--r-lg)',
          padding: '18px 20px',
          display: 'flex',
          gap: 26,
          alignItems: 'center',
        }}
      >
        <ScoreRing
          percent={published?.breakdown.mandatory.percent ?? null}
          maturity={published?.breakdown.maturity ?? null}
          size={104}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, maxWidth: 460 }}>
          {published === null ? (
            <>
              <span style={{ fontSize: 15, fontWeight: 600 }}>Not yet audited</span>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
                There is no closed audit, so this project has no maturity score. That is different
                from scoring zero — nobody has looked yet. Its first audit will open with{' '}
                <b>{data.scope.applicable} items in scope</b>, {data.scope.mandatory} of them
                mandatory.
              </p>
            </>
          ) : (
            <>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--ac)' }}>
                {published.breakdown.maturity === null
                  ? 'Scored'
                  : maturityLabel(published.breakdown.maturity)}
              </span>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
                Authority: <b style={{ color: 'var(--tx)' }}>audit #{published.auditNumber}</b>,
                closed {new Date(published.closedAt).toLocaleDateString('en-GB')} ·{' '}
                {published.breakdown.mandatory.verified} of {published.breakdown.mandatory.applicable}{' '}
                applicable mandatory items verified
                {published.breakdown.verifiedByAttestation > 0 && (
                  <>
                    {' '}· {published.breakdown.verifiedByAttestation} verified on an attestation
                  </>
                )}
              </p>
            </>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 20, textAlign: 'right' }}>
          {[
            ['In scope', data.scope.applicable],
            ['Mandatory', data.scope.mandatory],
            ['Not applicable', data.scope.notApplicable],
          ].map(([label, value]) => (
            <div key={label as string} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={LABEL}>{label}</span>
              <span className="mono" style={{ font: '600 16px/1 var(--font-sans)' }}>{value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={LABEL}>Checklist</span>
            <span className="mono" style={{ font: '600 16px/1 var(--font-sans)' }}>
              v{data.scope.checklistVersion}
            </span>
          </div>
        </div>
      </div>

      {data.scope.missingEvidenceExpectation > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 9,
            alignItems: 'flex-start',
            background: 'var(--wab)',
            border: '1px solid var(--wabd)',
            borderRadius: 'var(--r-lg)',
            padding: '10px 12px',
          }}
        >
          <span style={{ color: 'var(--wa)', marginTop: 1 }}><WarningGlyph size={14} /></span>
          <span style={{ fontSize: 11.5, color: 'var(--wa)', lineHeight: 1.55 }}>
            {data.scope.missingEvidenceExpectation} of the {data.scope.applicable} items in scope
            have no evidence expectation written, so auditors will have nothing quoted to check
            against and will fall back to attestations (ADR 0009).
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 16, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Scope by section">
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              {data.scope.sections.map((section) => (
                <div key={section.section} style={{ display: 'flex', alignItems: 'center', gap: 10, height: 26 }}>
                  <span className="mono" style={{ width: 14, fontWeight: 600, fontSize: 11.5 }}>
                    {section.section}
                  </span>
                  {/* No results yet, so the bar shows the shape of what is pending. */}
                  <SectionBar
                    counts={{ verified: 0, partial: 0, rejected: 0, waived: 0, pending: section.applicable }}
                  />
                  <span className="mono" style={{ width: 88, textAlign: 'right', fontSize: 11.5, color: 'var(--mu)' }}>
                    {section.mandatory} of {section.applicable}
                  </span>
                </div>
              ))}
              <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--mu)' }}>
                Mandatory of applicable, per section. Verified counts appear once an audit exists.
              </p>
            </div>
          </Card>

          <Card title={`Repositories (${data.repositories.length})`}>
            {data.repositories.map((repository) => (
              <div
                key={repository.id}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', borderBottom: '1px solid var(--bd)', fontSize: 12.5 }}
              >
                <span style={{ color: 'var(--mu)' }}><RepositoryGlyph size={13} /></span>
                <span className="mono" style={{ flex: 1 }}>{repository.pathWithNamespace}</span>
                {repository.isPrimary && <span style={CHIP}>primary</span>}
                <span className="mono" style={{ fontSize: 11.5, color: 'var(--mu)' }}>
                  {repository.defaultBranch}
                </span>
              </div>
            ))}
            <p style={{ margin: 0, padding: '9px 14px', fontSize: 11, color: 'var(--mu)' }}>
              Probes attach in Phase 4. Items A-01 and A-26 are answered against the primary
              repository alone.
            </p>
          </Card>

          <Card title={`Audits (${data.audits.length})`}>
            {data.audits.length === 0 ? (
              <div
                style={{
                  padding: '16px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 9,
                  alignItems: 'flex-start',
                }}
              >
                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--mu)', lineHeight: 1.6 }}>
                  No audits yet. Creating the first one materialises{' '}
                  <b style={{ color: 'var(--tx)' }}>{data.scope.applicable}</b> item results with
                  nothing inherited — there is no earlier audit to inherit from.
                </p>
                <Link
                  to={`/projects/${id}/audits/new`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 28,
                    padding: '0 11px',
                    borderRadius: 'var(--r-md)',
                    border: '1px solid var(--ac)',
                    background: 'var(--ac)',
                    color: '#fff',
                    font: '500 12px/1 var(--font-sans)',
                    textDecoration: 'none',
                  }}
                >
                  Start the first audit
                </Link>
              </div>
            ) : (
              data.audits.map((audit) => (
                // The whole row is the link. Reaching an audit was the gap that made the
                // three Phase 2 screens unreachable even after they were built.
                <Link
                  key={audit.id}
                  to={`/audits/${audit.id}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 14px',
                    borderBottom: '1px solid var(--bd)',
                    fontSize: 12.5,
                    color: 'var(--tx)',
                    textDecoration: 'none',
                  }}
                >
                  <span className="mono" style={{ width: 34, fontWeight: 600 }}>#{audit.number}</span>
                  <span style={{ flex: 1 }}>{audit.typeName} · {audit.periodLabel}</span>
                  <span style={CHIP}>{audit.status}</span>
                  <span className="mono" style={{ width: 44, textAlign: 'right' }}>
                    {audit.mandatoryPercent === null ? '—' : `${Math.round(audit.mandatoryPercent)}%`}
                  </span>
                  <span style={{ color: 'var(--mu)', fontSize: 11 }}>›</span>
                </Link>
              ))
            )}
          </Card>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card
            title="Team"
            action={
              <TextAction
                label="Assign a role"
                disabledReason={cannotManageTeam}
                onClick={() =>
                  setMemberDraft({
                    memberId: null,
                    userId: people.data?.[0]?.id ?? '',
                    role: 'PM',
                    isBackup: false,
                  })
                }
              />
            }
          >
            {data.members.map((member: Member) => (
              <div
                key={member.userId + member.role}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', borderBottom: '1px solid var(--bd)', fontSize: 12.5 }}
              >
                <span
                  className="mono"
                  title={teamRoleName(member.role)}
                  style={{ width: 30, fontSize: 11, fontWeight: 600, color: 'var(--mu)' }}
                >
                  {member.role}
                </span>
                <Avatar initials={member.initials} name={member.displayName} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {member.displayName}
                </span>
                {member.isBackup && <span style={CHIP}>backup</span>}
                {member.acknowledgedAt === null && (
                  <span style={{ ...CHIP, background: 'var(--wab)', color: 'var(--wa)', borderColor: 'var(--wabd)' }}>
                    unacknowledged
                  </span>
                )}
                {/*
                  Acknowledging is the holder's own act, so this appears only on their own
                  row. Without it, the reassignment rule above would be a one-way door:
                  cleared on every move and settable by nothing.
                */}
                {member.acknowledgedAt === null && member.userId === me.data?.id && (
                  <TextAction
                    label="Acknowledge"
                    disabledReason={acknowledge.isPending ? 'Saving…' : null}
                    onClick={() => acknowledge.mutate(member.id)}
                  />
                )}
                <TextAction
                  label="Change"
                  disabledReason={cannotManageTeam}
                  onClick={() =>
                    setMemberDraft({
                      memberId: member.id,
                      userId: member.userId,
                      role: member.role,
                      isBackup: member.isBackup,
                    })
                  }
                />
                <TextAction
                  label="Remove"
                  disabledReason={
                    cannotManageTeam ??
                    (data.members.length <= 1
                      ? 'This is the last assignment. A project with no members has nobody able to answer it.'
                      : removeMember.isPending
                        ? 'Removing…'
                        : null)
                  }
                  onClick={() => removeMember.mutate(member.id)}
                />
              </div>
            ))}
            {removeMember.isError && (
              <p style={{ margin: 0, padding: '8px 14px', fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
                {removeMember.error instanceof ApiClientError
                  ? removeMember.error.message
                  : 'The change was not saved.'}
              </p>
            )}
            <p style={{ margin: 0, padding: '9px 14px', fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
              An unacknowledged assignment is a reported gap (G-09), not a silent default.
              Moving a role to someone else clears its acknowledgement.
            </p>
          </Card>

          <Card title="Findings">
            <div style={{ padding: '12px 14px', display: 'flex', gap: 18 }}>
              {[
                ['Open', data.findings.open],
                ['Past deadline', data.findings.overdue],
                ['Critical', data.findings.critical],
              ].map(([label, value]) => (
                <div key={label as string} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={LABEL}>{label}</span>
                  <span
                    className="mono"
                    style={{ font: '600 20px/1 var(--font-sans)', color: (value as number) > 0 ? 'var(--ba)' : 'var(--tx)' }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Activity">
            {data.activity.length === 0 ? (
              <p style={{ margin: 0, padding: 14, fontSize: 12, color: 'var(--mu)' }}>Nothing yet.</p>
            ) : (
              data.activity.map((event, index) => (
                <div key={index} style={{ display: 'flex', gap: 9, padding: '8px 14px', borderBottom: '1px solid var(--bd)' }}>
                  <span style={{ fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.45 }}>
                    <b style={{ color: 'var(--tx)' }}>{event.actor ?? 'System'}</b> ·{' '}
                    {event.action.toLowerCase().replace(/_/g, ' ')}
                    <br />
                    {new Date(event.at).toLocaleString('en-GB')}
                  </span>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>

      {details !== null && (
        <Modal width={520}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Project details</h2>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
              What this project is called and how it is classified. The context profile — which
              decides <i>which items apply</i> — is edited separately, because that change needs
              a diff and a recorded reason.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '9px 10px', alignItems: 'center' }}>
            <span style={LABEL}>Name</span>
            <input
              value={details.name ?? ''}
              onChange={(e) => setDetails({ ...details, name: e.target.value })}
              style={INPUT}
            />

            <span style={LABEL}>Client</span>
            <input
              value={details.clientName ?? ''}
              onChange={(e) => setDetails({ ...details, clientName: e.target.value })}
              style={INPUT}
            />

            <span style={LABEL}>Criticality</span>
            <select
              value={details.criticality}
              onChange={(e) =>
                setDetails({ ...details, criticality: e.target.value as typeof details.criticality })
              }
              style={INPUT}
            >
              {CRITICALITY.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            <span style={LABEL}>Data class</span>
            <select
              value={details.dataClassification}
              onChange={(e) =>
                setDetails({
                  ...details,
                  dataClassification: e.target.value as typeof details.dataClassification,
                })
              }
              style={INPUT}
            >
              {DATA_CLASSIFICATION.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            <span style={LABEL}>Exposure</span>
            <select
              value={details.exposure}
              onChange={(e) =>
                setDetails({ ...details, exposure: e.target.value as typeof details.exposure })
              }
              style={INPUT}
            >
              {EXPOSURE.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>

            <span style={LABEL}>Hosting</span>
            <input
              value={details.hostingRegion ?? ''}
              onChange={(e) => setDetails({ ...details, hostingRegion: e.target.value })}
              style={INPUT}
            />

            <span style={LABEL}>GitLab group</span>
            <input
              value={details.gitlabGroupId ?? ''}
              placeholder="none"
              onChange={(e) =>
                setDetails({
                  ...details,
                  // Blank means "not linked", not "linked to an empty string".
                  gitlabGroupId: e.target.value.trim() === '' ? null : e.target.value,
                })
              }
              style={INPUT}
            />
          </div>

          <p style={{ margin: 0, fontSize: 11, color: 'var(--mu)', lineHeight: 1.5 }}>
            A rename is recorded on the project's trail. Reports already closed keep the name they
            were closed under — they are snapshots, not live views.
          </p>

          {saveDetails.isError && (
            <div style={{ fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
              {saveDetails.error instanceof ApiClientError
                ? saveDetails.error.message
                : 'The change failed.'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <PermissionButton onClick={() => setDetails(null)}>Cancel</PermissionButton>
            <PermissionButton
              variant="primary"
              onClick={() => saveDetails.mutate(details)}
              disabledReason={
                (details.name ?? '').trim().length < 2
                  ? 'A project needs a name of at least two characters.'
                  : (details.clientName ?? '').trim().length < 2
                    ? 'A client name of at least two characters is required.'
                    : saveDetails.isPending
                      ? 'Saving…'
                      : null
              }
            >
              Save
            </PermissionButton>
          </div>
        </Modal>
      )}

      {memberDraft !== null && (
        <Modal width={470}>
          {(() => {
            const original = data.members.find((entry) => entry.id === memberDraft.memberId);
            // Only a change of person or role is a reassignment; primary/backup is not.
            const reassigning =
              original !== undefined &&
              (original.userId !== memberDraft.userId || original.role !== memberDraft.role);

            return (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>
                    {memberDraft.memberId === null ? 'Assign a role' : 'Change this assignment'}
                  </h2>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
                    One row per person and role. Somebody who is both Tech Lead and Security Lead
                    holds two assignments, because acknowledging one is not acknowledging the other.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '9px 10px', alignItems: 'center' }}>
                  <span style={LABEL}>Person</span>
                  <select
                    value={memberDraft.userId}
                    onChange={(e) => setMemberDraft({ ...memberDraft, userId: e.target.value })}
                    style={INPUT}
                  >
                    {(people.data ?? []).map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.displayName} · {person.email}
                      </option>
                    ))}
                  </select>

                  <span style={LABEL}>Role</span>
                  <select
                    value={memberDraft.role}
                    onChange={(e) =>
                      setMemberDraft({ ...memberDraft, role: e.target.value as TeamRole_ })
                    }
                    style={INPUT}
                  >
                    {TEAM_ROLES.map((entry) => (
                      <option key={entry.role} value={entry.role}>
                        {entry.role} — {entry.name}
                      </option>
                    ))}
                  </select>

                  <span />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                    <input
                      type="checkbox"
                      checked={memberDraft.isBackup}
                      onChange={(e) => setMemberDraft({ ...memberDraft, isBackup: e.target.checked })}
                    />
                    Backup for this role, not the primary owner
                  </label>
                </div>

                {reassigning && original?.acknowledgedAt !== null && (
                  <div
                    style={{
                      border: '1px solid var(--wabd)',
                      background: 'var(--wab)',
                      borderRadius: 'var(--r-lg)',
                      padding: '10px 11px',
                      fontSize: 11.5,
                      color: 'var(--wa)',
                      lineHeight: 1.55,
                    }}
                  >
                    This assignment was acknowledged. Moving it clears that — an owner who has not
                    acknowledged is not an owner, and item A-14 stays Partial until the new owner
                    does. It becomes a reported gap (G-09) in the meantime.
                  </div>
                )}

                {saveMember.isError && (
                  <div style={{ fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
                    {saveMember.error instanceof ApiClientError
                      ? saveMember.error.message
                      : 'The change failed.'}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <PermissionButton onClick={() => setMemberDraft(null)}>Cancel</PermissionButton>
                  <PermissionButton
                    variant="primary"
                    onClick={() => saveMember.mutate(memberDraft)}
                    disabledReason={
                      memberDraft.userId === ''
                        ? 'Choose who holds this role.'
                        : saveMember.isPending
                          ? 'Saving…'
                          : null
                    }
                  >
                    {memberDraft.memberId === null ? 'Assign' : 'Save'}
                  </PermissionButton>
                </div>
              </>
            );
          })()}
        </Modal>
      )}

      {showContextEditor && draft !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,24,39,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            zIndex: 10,
          }}
        >
          <div
            style={{
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 'var(--r-lg)',
              width: 620,
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Change the context profile</h2>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
                This changes which items apply. The diff below is computed before anything is
                written, and the reason you give is recorded against the project permanently.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {FLAG_LABELS.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
                  <input
                    type="checkbox"
                    checked={draft[key] === true}
                    onChange={(e) => setDraft({ ...draft, [key]: e.target.checked })}
                  />
                  {label}
                </label>
              ))}
            </div>

            {preview.data !== undefined && (
              <div
                style={{
                  border: '1px dashed var(--inbd)',
                  background: 'var(--acs)',
                  borderRadius: 'var(--r-lg)',
                  padding: '11px 12px',
                  fontSize: 11.5,
                  lineHeight: 1.6,
                }}
              >
                <b>
                  {preview.data.diff.totals.before.applicable} → {preview.data.diff.totals.after.applicable} items
                  in scope
                </b>{' '}
                ({preview.data.diff.totals.before.mandatory} → {preview.data.diff.totals.after.mandatory} mandatory)
                <br />
                {preview.data.diff.entering.length > 0 && (
                  <>
                    Entering ({preview.data.diff.entering.length}):{' '}
                    <span className="mono">{preview.data.diff.entering.slice(0, 12).join(', ')}</span>
                    {preview.data.diff.entering.length > 12 && ' …'}
                    <br />
                  </>
                )}
                {preview.data.diff.leaving.length > 0 && (
                  <>
                    Leaving ({preview.data.diff.leaving.length}):{' '}
                    <span className="mono">{preview.data.diff.leaving.slice(0, 12).join(', ')}</span>
                    {preview.data.diff.leaving.length > 12 && ' …'}
                    <br />
                  </>
                )}
                {preview.data.diff.entering.length === 0 && preview.data.diff.leaving.length === 0 && (
                  <>Nothing changes — no item enters or leaves scope.</>
                )}
                {preview.data.mandatoryLeaving.length > 0 && (
                  <span style={{ color: 'var(--wa)' }}>
                    <br />
                    {preview.data.mandatoryLeaving.length} of the leaving items are mandatory, so the
                    score's denominator shrinks.
                  </span>
                )}
              </div>
            )}

            <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={LABEL}>Reason (recorded permanently)</span>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                rows={3}
                placeholder="The client confirmed the portal no longer stores personal data after the March migration."
                style={{
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--r-lg)',
                  background: 'var(--sf)',
                  color: 'var(--tx)',
                  font: '400 12.5px/1.5 var(--font-sans)',
                  padding: '8px 10px',
                  resize: 'vertical',
                }}
              />
            </label>

            {commit.isError && (
              <div style={{ fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
                {commit.error instanceof ApiClientError ? commit.error.message : 'The change failed.'}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <PermissionButton
                onClick={() => {
                  setShowContextEditor(false);
                  setDraft(null);
                  setJustification('');
                }}
              >
                Cancel
              </PermissionButton>
              <PermissionButton
                variant="primary"
                onClick={() => commit.mutate()}
                disabledReason={
                  justification.trim().length < 10
                    ? 'A reason of at least a sentence is required.'
                    : commit.isPending
                      ? 'Saving…'
                      : null
                }
              >
                Save the change
              </PermissionButton>
            </div>
          </div>
        </div>
      )}

      {showCategoryEditor && categoryDraft !== null && (
        <Modal width={620}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 style={{ margin: 0, font: '600 16px/1.2 var(--font-sans)' }}>Change the category scope</h2>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--mu)', lineHeight: 1.55 }}>
              This changes which technology areas a new audit covers — a second, coarser scope
              gate beside the context profile. A closed audit keeps the selection it was created
              with, so this never moves an existing audit's gate. The diff below is computed
              before anything is written, and the reason you give is recorded against the project
              permanently.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
              <input
                type="checkbox"
                checked={categoryDraft === 'ALL'}
                onChange={(e) => setCategoryDraft(e.target.checked ? 'ALL' : [CHECKLIST_CATEGORY_NAMES[0]!.category])}
              />
              All categories
            </label>
            {categoryDraft !== 'ALL' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingLeft: 18 }}>
                {CHECKLIST_CATEGORY_NAMES.map(({ category, name }) => {
                  const selected = categoryDraft.includes(category);
                  return (
                    <label key={category} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5 }}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...categoryDraft, category]
                            : categoryDraft.filter((entry) => entry !== category);
                          setCategoryDraft(next.length === 0 ? categoryDraft : next);
                        }}
                      />
                      {name}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {categoryPreview.data !== undefined && (
            <div
              style={{
                border: '1px dashed var(--inbd)',
                background: 'var(--acs)',
                borderRadius: 'var(--r-lg)',
                padding: '11px 12px',
                fontSize: 11.5,
                lineHeight: 1.6,
              }}
            >
              <b>
                {categoryPreview.data.diff.totals.before.applicable} →{' '}
                {categoryPreview.data.diff.totals.after.applicable} items in scope
              </b>{' '}
              ({categoryPreview.data.diff.totals.before.mandatory} →{' '}
              {categoryPreview.data.diff.totals.after.mandatory} mandatory)
              <br />
              {categoryPreview.data.diff.entering.length > 0 && (
                <>
                  Entering ({categoryPreview.data.diff.entering.length}):{' '}
                  <span className="mono">{categoryPreview.data.diff.entering.slice(0, 12).join(', ')}</span>
                  {categoryPreview.data.diff.entering.length > 12 && ' …'}
                  <br />
                </>
              )}
              {categoryPreview.data.diff.leaving.length > 0 && (
                <>
                  Leaving ({categoryPreview.data.diff.leaving.length}):{' '}
                  <span className="mono">{categoryPreview.data.diff.leaving.slice(0, 12).join(', ')}</span>
                  {categoryPreview.data.diff.leaving.length > 12 && ' …'}
                  <br />
                </>
              )}
              {categoryPreview.data.diff.entering.length === 0 && categoryPreview.data.diff.leaving.length === 0 && (
                <>Nothing changes — no item enters or leaves scope.</>
              )}
              {categoryPreview.data.mandatoryLeaving.length > 0 && (
                <span style={{ color: 'var(--wa)' }}>
                  <br />
                  {categoryPreview.data.mandatoryLeaving.length} of the leaving items are mandatory, so the
                  score's denominator shrinks.
                </span>
              )}
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={LABEL}>Reason (recorded permanently)</span>
            <textarea
              value={categoryJustification}
              onChange={(e) => setCategoryJustification(e.target.value)}
              rows={3}
              placeholder="The client's infrastructure is managed by a third party, so backup/DR items don't apply here."
              style={{
                border: '1px solid var(--bd)',
                borderRadius: 'var(--r-lg)',
                background: 'var(--sf)',
                color: 'var(--tx)',
                font: '400 12.5px/1.5 var(--font-sans)',
                padding: '8px 10px',
                resize: 'vertical',
              }}
            />
          </label>

          {commitCategoryScope.isError && (
            <div style={{ fontSize: 11.5, color: 'var(--ba)', lineHeight: 1.5 }}>
              {commitCategoryScope.error instanceof ApiClientError
                ? commitCategoryScope.error.message
                : 'The change failed.'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <PermissionButton
              onClick={() => {
                setShowCategoryEditor(false);
                setCategoryDraft(null);
                setCategoryJustification('');
              }}
            >
              Cancel
            </PermissionButton>
            <PermissionButton
              variant="primary"
              onClick={() => commitCategoryScope.mutate()}
              disabledReason={
                categoryJustification.trim().length < 10
                  ? 'A reason of at least a sentence is required.'
                  : commitCategoryScope.isPending
                    ? 'Saving…'
                    : null
              }
            >
              Save the change
            </PermissionButton>
          </div>
        </Modal>
      )}
    </div>
  );
}
